// #40: the pure logic behind stopping automatic payments. A signatory starts a stop from My Account (Stop automatic
// payments); its card says what stopping means and asks the signatories to sign it. The stop is an allowance of 0 from the
// company account to its operating account on the public ledger, whose memo says who stopped them; nodeStream's platform
// account pays every fee. Nothing is charged or topped up from the moment enough signatories have signed, and once it has
// run automatic payments are off and the card is removed. The server decides (ns-lambda-ts public-ledger/autopay/
// {start-change,propose-stop,apply,stop-finish}.ts); this only chooses what the cards show and offer.
// Wording not yet reviewed.
import { formatCredits } from './public-ledger-credits.js';
import type { AutoPaymentRow } from './auto-payments.js';

export const STOP_INTRO =
  "Stop your company's automatic payments. The signatories sign the stop on the public ledger; payments stop once it's signed.";

// What My Account's Stop button says, with an Open link to its card, when a stop already waits for signatures
export const STOP_WAITING_TEXT = 'A stop of automatic payments is already waiting for signatures.';

// The stop card's button, and its line once pressed
export const STOP_BUTTON = 'Ask the signatories to stop them';
export const STOP_PROPOSED_TEXT = 'The stop is waiting for the signatories to sign it.';

// A closed stop card's last line once its stop has ended
export const STOP_DONE_TEXT = {
  STOPPED: 'Automatic payments are stopped.',
  NOT_STOPPED: "The stop didn't go through, so automatic payments carry on.",
  CANCELLED: 'This stop was cancelled.',
} as const;

// What the stop card says while it can't propose
export const STOP_NOT_ACTIVE_TEXT = "Automatic payments aren't on for your company, so there is nothing to stop.";
export const STOP_CHANGE_OPEN_TEXT =
  'Another change to automatic payments is waiting for signatures. You can stop them once it is signed or ended: any ' +
  'signatory can end it by rejecting it.';

// The server's own refusal when the stop no longer waits (public-ledger/autopay/prepare-signature)
export const STOP_GONE_COPY = 'This stop is no longer waiting for signatures.';
// The memo served differs from the one the card showed
export const STOP_CHANGED_COPY =
  'This stop was updated just as you signed, so nothing was signed. Please check it again and sign.';

// What the signing card says above the memo, naming who asked to stop them
export function stopSignCopy(name: string | null | undefined): string {
  const who = name?.trim() || 'A signatory';
  return `${who} asked to stop the company's automatic payments. Your signature approves the stop on the company's public ledger account, where it is recorded as below. Once the signatories have signed, no more card charges or top-ups are made and the card is removed. nodeStream pays every public ledger fee for the stop.`;
}

/**
 * Why the card must not sign what the server prepared, or null when it may: the memo the server signs for must be the one
 * the card showed. No memo served means the stop no longer waits.
 */
export function stopSignRefusal(
  servedMemo: string | null | undefined,
  shownMemo: string | null | undefined,
): string | null {
  if (servedMemo && shownMemo && servedMemo === shownMemo) return null;
  if (!servedMemo) return STOP_GONE_COPY;
  return STOP_CHANGED_COPY;
}

/**
 * What stopping means, for the stop card and the signing card, from the company's balances in credits (null when not read)
 * and its card ("Visa •••• 4242", or null). DECISION 1: credits in the company account stay there, unusable until automatic
 * payments are set up again, said with the amount before anyone signs. DECISION 3: a payment already under way when the
 * stop is signed may still be taken, and is delivered as credits.
 */
export function stopConsequences(input: {
  companyCredits: number | null;
  operatingCredits: number | null;
  card: string | null;
}): string[] {
  const credits = (value: number | null) => (value === null ? null : `${formatCredits(value)} credits`);
  const company = credits(input.companyCredits);
  const operating = credits(input.operatingCredits);
  return [
    'Automatic payments carry on until enough signatories have signed. From then, no more card charges or top-ups are made.',
    input.card ? `The ${input.card} is then removed.` : 'The card is then removed.',
    operating
      ? `The ${operating} in the operating account can still be used for public ledger actions.`
      : 'Credits already in the operating account can still be used for public ledger actions.',
    company
      ? `The ${company} in the company account stay there, and can't be used until automatic payments are set up again.`
      : "Credits in the company account stay there, and can't be used until automatic payments are set up again.",
    'A payment already under way when the stop is signed may still be taken, and is delivered as credits.',
    'A signatory can set automatic payments up again from My Account.',
  ];
}

export type StopStage = 'DONE' | 'CLAIM' | 'LOADING' | 'PROPOSED' | 'NOT_ACTIVE' | 'CHANGE_OPEN' | 'LOCKED' | 'READY';

/**
 * Where the Stop automatic payments card is:
 * - DONE: the job is finished;
 * - CLAIM: nobody holds it (it was released);
 * - LOADING: the automatic payments row hasn't been read yet;
 * - PROPOSED: this stop is recorded (propose-stop answered, or the row names this person's stop); the server completes the
 *   job a moment later;
 * - NOT_ACTIVE: automatic payments aren't on (propose-stop refuses);
 * - CHANGE_OPEN: another change waits for signatures, or a card is held for a change of card (propose-stop refuses);
 * - LOCKED: a signatory change is pending (propose-stop refuses);
 * - READY: the holder can ask the signatories to stop them.
 */
export function stopStage(input: {
  completed: boolean;
  claimed: boolean;
  row: (AutoPaymentRow & { cardSwitch?: { workflowId?: string | null } | null }) | null | undefined;
  personId: string | null | undefined;
  proposedTransactionId: string | null;
  changePending: boolean;
  rowLoaded?: boolean;
}): StopStage {
  if (input.completed) return 'DONE';
  if (!input.claimed) return 'CLAIM';
  if (input.proposedTransactionId) return 'PROPOSED';
  if (input.rowLoaded === false) return 'LOADING';
  const pending = input.row?.pendingChange;
  if (pending?.transactionId) {
    const mine = pending.kind === 'STOP' && !!input.personId && pending.requestedBy === input.personId;
    return mine ? 'PROPOSED' : 'CHANGE_OPEN';
  }
  if (input.row?.setupState !== 'ACTIVE') return 'NOT_ACTIVE';
  if (input.row?.cardSwitch?.workflowId) return 'CHANGE_OPEN';
  if (input.changePending) return 'LOCKED';
  return 'READY';
}

/**
 * The line a closed stop card shows, from the stop workflow: `stopped`, `not-stopped`, or `cancelled` / a CANCELLED
 * workflow. While the workflow is still PENDING after the stop was proposed (TRANSACTION_ID), it waits for the signatories.
 * Null when the workflow isn't read, or is still ending some other way.
 */
export function stopDoneText(
  workflow:
    | { status?: string | null; outcome?: string | null; metadata?: { key: string; value: string | null }[] | null }
    | null
    | undefined,
): string | null {
  if (!workflow) return null;
  const outcome = workflow.outcome ?? null;
  if (outcome === 'stopped') return STOP_DONE_TEXT.STOPPED;
  if (outcome === 'not-stopped') return STOP_DONE_TEXT.NOT_STOPPED;
  if (outcome === 'cancelled' || workflow.status === 'CANCELLED') return STOP_DONE_TEXT.CANCELLED;
  const sent = (workflow.metadata ?? []).some((m) => m?.key === 'TRANSACTION_ID' && !!m.value);
  if (workflow.status === 'PENDING' && !outcome && sent) return STOP_PROPOSED_TEXT;
  return null;
}
